/* eslint-disable max-len */
import {SERVICE_NAMES} from '../constants';
import {createApiCall} from '../utils';
import type {ServiceName} from '../constants';
import type {
    ApiCallOptions,
    PaginatedResponse,
    Transaction,
    Template,
    DeclineReason,
    CreateTransactionRequest,
    UpdateTransactionRequest,
    CreateTemplateRequest,
    UpdateTemplateRequest,
    CloneToTransactionRequest,
    CloneAsTemplateRequest,
    FastTrackTemplateRequest,
    CreateDeclineReasonRequest,
    TransactionsListParams,
    TemplatesListParams,
    TransactionReportsParams,
    TransactionReports,
    AccountTransactionReport,
    UsageReport,
    UsageReportEntry,
    UsageReportsListParams,
    UserUsageReportParams,
    AwaitingSignaturesParams,
    TransactionsOverviewParams,
    TransactionOverview,
    TransactionOwnersParams,
    TransactionOwner,
    TransactionArchivePreviewRequest,
    TransactionArchivePreviewResource
} from './api.d';

/**
 * Transaction Service API client.
 *
 * Source: https://transaction-service.dev.validsign.eu/api/documentation/api.json
 * Synced: 2026-06-29
 */

const createTransactionApiCall = <T = any>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
    endpoint: string
) => createApiCall<T>(SERVICE_NAMES.TRANSACTION as ServiceName, method, endpoint);

export const transactionApi = {
    // ========== Transactions ==========

    /** GET /accounts/{accountId}/transactions */
    getTransactions: ({accountId, params, ...options}: {accountId: string; params?: TransactionsListParams} & ApiCallOptions) =>
        createTransactionApiCall<PaginatedResponse<Transaction>>('GET', `accounts/${accountId}/transactions`)({...options, params}),

    /** POST /accounts/{accountId}/transactions */
    createTransaction: ({accountId, data, ...options}: {accountId: string; data: CreateTransactionRequest} & ApiCallOptions) =>
        createTransactionApiCall<Transaction>('POST', `accounts/${accountId}/transactions`)({...options, data}),

    /** GET /accounts/{accountId}/transactions/{transaction} */
    getTransaction: ({accountId, transactionId, ...options}: {accountId: string; transactionId: string} & ApiCallOptions) =>
        createTransactionApiCall<Transaction>('GET', `accounts/${accountId}/transactions/${transactionId}`)({...options}),

    /** PUT /accounts/{accountId}/users/{userId}/transactions/{transaction} */
    updateTransaction: ({
        accountId, userId, transactionId, data, ...options
    }: {accountId: string; userId: string; transactionId: string; data: UpdateTransactionRequest} & ApiCallOptions) =>
        createTransactionApiCall<Transaction>('PUT', `accounts/${accountId}/users/${userId}/transactions/${transactionId}`)({...options, data}),

    /** DELETE /accounts/{accountId}/users/{userId}/transactions/{transaction} */
    deleteTransaction: ({
        accountId, userId, transactionId, ...options
    }: {accountId: string; userId: string; transactionId: string} & ApiCallOptions) =>
        createTransactionApiCall<void>('DELETE', `accounts/${accountId}/users/${userId}/transactions/${transactionId}`)({...options}),

    /** DELETE /accounts/{accountId}/users/{userId}/transactions/{transaction}/force — permanently delete, bypassing trash */
    forceDeleteTransaction: ({
        accountId, userId, transactionId, ...options
    }: {accountId: string; userId: string; transactionId: string} & ApiCallOptions) =>
        createTransactionApiCall<void>('DELETE', `accounts/${accountId}/users/${userId}/transactions/${transactionId}/force`)({...options}),

    /** PUT /accounts/{accountId}/users/{userId}/transactions/{transaction}/trash — move to trash */
    trashTransaction: ({
        accountId, userId, transactionId, ...options
    }: {accountId: string; userId: string; transactionId: string} & ApiCallOptions) =>
        createTransactionApiCall<Transaction>('PUT', `accounts/${accountId}/users/${userId}/transactions/${transactionId}/trash`)({...options}),

    /** PUT /accounts/{accountId}/users/{userId}/transactions/{transaction}/restore — restore from trash */
    restoreTransaction: ({
        accountId, userId, transactionId, ...options
    }: {accountId: string; userId: string; transactionId: string} & ApiCallOptions) =>
        createTransactionApiCall<Transaction>('PUT', `accounts/${accountId}/users/${userId}/transactions/${transactionId}/restore`)({...options}),

    /** PUT /accounts/{accountId}/users/{userId}/transactions/{transaction}/archive */
    archiveTransaction: ({
        accountId, userId, transactionId, ...options
    }: {accountId: string; userId: string; transactionId: string} & ApiCallOptions) =>
        createTransactionApiCall<Transaction>('PUT', `accounts/${accountId}/users/${userId}/transactions/${transactionId}/archive`)({...options}),

    /** PUT /accounts/{accountId}/users/{userId}/transactions/{transaction}/unarchive */
    unarchiveTransaction: ({
        accountId, userId, transactionId, ...options
    }: {accountId: string; userId: string; transactionId: string} & ApiCallOptions) =>
        createTransactionApiCall<Transaction>('PUT', `accounts/${accountId}/users/${userId}/transactions/${transactionId}/unarchive`)({...options}),

    /** PATCH /accounts/{accountId}/users/{userId}/transactions/{transaction}/send — DRAFT → SENT */
    sendTransaction: ({
        accountId, userId, transactionId, ...options
    }: {accountId: string; userId: string; transactionId: string} & ApiCallOptions) =>
        createTransactionApiCall<Transaction>('PATCH', `accounts/${accountId}/users/${userId}/transactions/${transactionId}/send`)({...options}),

    /** PUT /accounts/{accountId}/users/{userId}/transactions/{transaction}/set-to-draft */
    setTransactionToDraft: ({
        accountId, userId, transactionId, ...options
    }: {accountId: string; userId: string; transactionId: string} & ApiCallOptions) =>
        createTransactionApiCall<Transaction>('PUT', `accounts/${accountId}/users/${userId}/transactions/${transactionId}/set-to-draft`)({...options}),

    /** GET /accounts/{accountId}/transactions/{transaction}/status */
    getTransactionStatus: ({accountId, transactionId, ...options}: {accountId: string; transactionId: string} & ApiCallOptions) =>
        createTransactionApiCall<any>('GET', `accounts/${accountId}/transactions/${transactionId}/status`)({...options}),

    /** POST /accounts/{accountId}/transactions/{transaction}/clone — clone an existing transaction */
    cloneTransaction: ({
        accountId, transactionId, data, ...options
    }: {accountId: string; transactionId: string; data?: CloneToTransactionRequest} & ApiCallOptions) =>
        createTransactionApiCall<Transaction>('POST', `accounts/${accountId}/transactions/${transactionId}/clone`)({...options, data}),

    // ========== Templates ==========

    /** GET /accounts/{accountId}/templates */
    getTemplates: ({accountId, params, ...options}: {accountId: string; params?: TemplatesListParams} & ApiCallOptions) =>
        createTransactionApiCall<PaginatedResponse<Template>>('GET', `accounts/${accountId}/templates`)({...options, params}),

    /** GET /accounts/{accountId}/templates/{template} */
    getTemplate: ({accountId, templateId, ...options}: {accountId: string; templateId: string} & ApiCallOptions) =>
        createTransactionApiCall<Template>('GET', `accounts/${accountId}/templates/${templateId}`)({...options}),

    /** POST /accounts/{accountId}/users/{userId}/templates */
    createTemplate: ({
        accountId, userId, data, ...options
    }: {accountId: string; userId: string; data: CreateTemplateRequest} & ApiCallOptions) =>
        createTransactionApiCall<Template>('POST', `accounts/${accountId}/users/${userId}/templates`)({...options, data}),

    /** PUT /accounts/{accountId}/users/{userId}/templates/{template} */
    updateTemplate: ({
        accountId, userId, templateId, data, ...options
    }: {accountId: string; userId: string; templateId: string; data: UpdateTemplateRequest} & ApiCallOptions) =>
        createTransactionApiCall<Template>('PUT', `accounts/${accountId}/users/${userId}/templates/${templateId}`)({...options, data}),

    /** DELETE /accounts/{accountId}/users/{userId}/templates/{template} */
    deleteTemplate: ({
        accountId, userId, templateId, ...options
    }: {accountId: string; userId: string; templateId: string} & ApiCallOptions) =>
        createTransactionApiCall<void>('DELETE', `accounts/${accountId}/users/${userId}/templates/${templateId}`)({...options}),

    /** POST /accounts/{accountId}/templates/{template}/clone */
    cloneTemplate: ({
        accountId, templateId, data, ...options
    }: {accountId: string; templateId: string; data?: CloneToTransactionRequest} & ApiCallOptions) =>
        createTransactionApiCall<Transaction>('POST', `accounts/${accountId}/templates/${templateId}/clone`)({...options, data}),

    /** POST /accounts/{accountId}/templates/{template}/clone-as-template */
    cloneAsTemplate: ({
        accountId, templateId, data, ...options
    }: {accountId: string; templateId: string; data?: CloneAsTemplateRequest} & ApiCallOptions) =>
        createTransactionApiCall<Transaction>('POST', `accounts/${accountId}/templates/${templateId}/clone-as-template`)({...options, data}),

    /** POST /accounts/{accountId}/templates/{template}/fast-track — fast-track a template to a new transaction */
    fastTrackTemplate: ({
        accountId, templateId, data, ...options
    }: {accountId: string; templateId: string; data: FastTrackTemplateRequest} & ApiCallOptions) =>
        createTransactionApiCall<Transaction>('POST', `accounts/${accountId}/templates/${templateId}/fast-track`)({...options, data}),

    // ========== Decline reasons ==========

    /** GET /accounts/{accountId}/transactions/{transaction}/decline-reasons */
    getDeclineReasons: ({accountId, transactionId, ...options}: {accountId: string; transactionId: string} & ApiCallOptions) =>
        createTransactionApiCall<DeclineReason[]>('GET', `accounts/${accountId}/transactions/${transactionId}/decline-reasons`)({...options}),

    /** POST /accounts/{accountId}/transactions/{transaction}/decline-reasons */
    createDeclineReason: ({
        accountId, transactionId, data, ...options
    }: {accountId: string; transactionId: string; data: CreateDeclineReasonRequest} & ApiCallOptions) =>
        createTransactionApiCall<DeclineReason>('POST', `accounts/${accountId}/transactions/${transactionId}/decline-reasons`)({...options, data}),

    // ========== Archive ==========

    /**
     * POST /accounts/{accountId}/transactions/archive/preview — total transaction
     * count for the given archive scope. Response body is `{data: {...}}`.
     */
    getTransactionsArchivePreview: ({
        accountId, data, ...options
    }: {accountId: string; data: TransactionArchivePreviewRequest} & ApiCallOptions) =>
        createTransactionApiCall<{data: TransactionArchivePreviewResource}>('POST', `accounts/${accountId}/transactions/archive/preview`)({...options, data}),

    // ========== Cross-account (no accountId in URL) ==========

    /** GET /transactions — list all transactions or templates the caller can access */
    getAllTransactionsOrTemplates: ({params, ...options}: {params?: TransactionReportsParams} & ApiCallOptions = {}) =>
        createTransactionApiCall<PaginatedResponse<Transaction>>('GET', 'transactions')({...options, params}),

    /** GET /transactions/{transaction} */
    getTransactionOrTemplateById: ({transactionId, ...options}: {transactionId: string} & ApiCallOptions) =>
        createTransactionApiCall<Transaction>('GET', `transactions/${transactionId}`)({...options}),

    /** GET /transactions/awaiting-signatures */
    getAwaitingSignatures: ({params, ...options}: {params?: AwaitingSignaturesParams} & ApiCallOptions = {}) =>
        createTransactionApiCall<PaginatedResponse<Transaction>>('GET', 'transactions/awaiting-signatures')({...options, params}),

    /** GET /transactions/overview */
    getTransactionsOverview: ({params, ...options}: {params?: TransactionsOverviewParams} & ApiCallOptions = {}) =>
        createTransactionApiCall<TransactionOverview>('GET', 'transactions/overview')({...options, params}),

    /** GET /transactions/owners — distinct owners across the account's transactions */
    getTransactionOwners: ({params, ...options}: {params: TransactionOwnersParams} & ApiCallOptions) =>
        createTransactionApiCall<{data: TransactionOwner[]}>('GET', 'transactions/owners')({...options, params}),

    // ========== Reports ==========

    /** GET /transactions/reports */
    getTransactionReports: ({...options}: ApiCallOptions = {}) =>
        createTransactionApiCall<TransactionReports>('GET', 'transactions/reports')({...options}),

    /** GET /accounts/{account}/transactions/reports */
    getAccountTransactionReports: ({accountId, ...options}: {accountId: string} & ApiCallOptions) =>
        createTransactionApiCall<AccountTransactionReport>('GET', `accounts/${accountId}/transactions/reports`)({...options}),

    /** GET /accounts/{accountId}/transactions/usage-reports */
    getUsageReports: ({accountId, params, ...options}: {accountId: string; params: UsageReportsListParams} & ApiCallOptions) =>
        createTransactionApiCall<PaginatedResponse<UsageReport>>('GET', `accounts/${accountId}/transactions/usage-reports`)({...options, params}),

    /** GET /accounts/{accountId}/transactions/usage-reports/download — returns a binary blob */
    downloadUsageReport: ({accountId, params, ...options}: {accountId: string; params: UsageReportsListParams} & ApiCallOptions) =>
        createTransactionApiCall<Blob>('GET', `accounts/${accountId}/transactions/usage-reports/download`)({
            ...options,
            params,
            responseType: 'blob'
        }),

    /** GET /accounts/{accountId}/transactions/usage-reports/user/{userId} */
    getUserUsageReport: ({
        accountId, userId, params, ...options
    }: {accountId: string; userId: string; params: UserUsageReportParams} & ApiCallOptions) =>
        createTransactionApiCall<PaginatedResponse<UsageReportEntry>>('GET', `accounts/${accountId}/transactions/usage-reports/user/${userId}`)({...options, params})
};
